import React, { useCallback, useEffect, useRef, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import VaultPassphraseSetupModal from './VaultPassphraseSetupModal';
import VaultPassphraseUnlockModal from './VaultPassphraseUnlockModal';
import VaultBackupRestoreModal from './VaultBackupRestoreModal';
import VaultUpdateRequiredScreen from './VaultUpdateRequiredScreen';
import { isVaultUpdateRequired } from '../services/vaultUpdateRequired';
import VaultColdStartUnlockScreen from './VaultColdStartUnlockScreen';
import VaultColdStartConflictScreen from './VaultColdStartConflictScreen';
import VaultColdStartOfflineScreen from './VaultColdStartOfflineScreen';
import {
  VAULT_GATE_EVENT,
  VAULT_GATE_UNLOCKED_EVENT,
  VAULT_GATE_DISMISSED_EVENT,
  refreshVault,
  refreshVaultMaster,
  useVaultMasterStore,
  onVaultUnlocked,
} from '../store/vaultStore';
import { useAuthStore } from '../store/authStore';
import { ensureVaultSync } from '../services/vaultSync';
import { probeColdStart, unlockColdStart, finishColdStart } from '../services/vaultColdStart';
import { recordFeature, ACTION } from '../services/telemetry';

/**
 * The one place any surface can ask for the vault passphrase
 * (CONTRACT §6.1, §6.7).
 *
 * It exists because the thing that needs the vault open is very often not
 * the vault panel. The FTP sidebar, a chat secret card — each of them used to
 * swallow
 * `passphrase_required` and shows nothing at all, which is a dead end with no
 * error anywhere. They dispatch `vault-gate-open`; this host decides which
 * screen that means and shows it. The window event is the ENTIRE interface —
 * callers import nothing from here and this imports nothing from them.
 *
 * Which screen is decided from `vault_master_status` + `vault_status`, never
 * from the caller:
 *
 * | mode | initialized | unlocked | screen |
 * |---|---|---|---|
 * | keyMissing         | —     | —     | backup restore |
 * | not protected      | —     | false | bootstrap ceremony (vault-storage-transition §12 Step 1) |
 * | keyringOnly        | false | true  | first-time setup (§6.2) |
 * | keyringOnly        | true  | true  | migration (§6.5) |
 * | protected          | —     | —     | unlock (§6.3) |
 *
 * `keyMissing` is checked FIRST and is not a variant of `keyringOnly`, even
 * though `mode` reports `keyringOnly` for it. In that state the file has no
 * wraps and the keychain says it should, so no secret the user holds can open
 * it — and the setup modal, which is what the mode alone would have selected,
 * offers to overwrite the very file that needs restoring and dies on its first
 * call with a generic error.
 *
 * The bootstrap row is checked before `keyringOnly`'s two rows, and is picked
 * on `!unlocked` alone (not on `initialized`): `master_prepare_core`
 * (`vault_master_prepare`, the setup/migrate rows below) calls `require_dek`
 * and has nothing to derive a wrap from unless this scope is ALREADY
 * unlocked — and in this build, since vault-storage-transition phase 6
 * deleted the OS keychain, the ONLY way an unprotected scope's DEK is ever in
 * memory is by having just been bootstrapped (`vault.rs::resolve_dek`'s
 * `Some(block)` arm is the sole writer of the in-memory key, and it requires
 * `protected` already true). So "not protected AND not unlocked" is exactly
 * — and only — the state `vault_unlock` answers `vault_needs_bootstrap` for
 * (a rowless scope, or one left bare-and-empty by a pre-phase-5 build); the
 * setup/migrate rows are unreachable production code today, kept for
 * `device_migration.rs`'s real account-import path once it lands (not yet
 * built — TSW3-MIGRATION.md §12 "superseded, not duplicated, once Step 4
 * lands").
 *
 * ### Multi-device sync (`app/plans/01_08_2026/vault-multidevice/PLAN.md` §7)
 *
 * "not protected AND not unlocked" is exactly ONE table row above, but it is
 * two different situations for a signed-in account: a genuinely fresh
 * account (bootstrap), or an account whose vault already exists on another
 * device (cold start). `openFor` probes for the second case — signed in
 * only. `mode` alone cannot tell these apart from a THIRD case the probe
 * also has to catch: this device's own row is already protected, but under
 * a DIFFERENT `vault_id` than the signed-in account's (`found`/AdoptInto
 * Existing when nothing local would be lost, `conflict` when it would) —
 * `mode` only reports whether a kdf block exists, never which vault it
 * belongs to. So `found`/`conflict` are checked BEFORE the `protected` row,
 * and only `offline`/`not_found` are checked after it, where the `protected`
 * row is correct exactly because the probe already ruled out a mismatch
 * (review fix; see the two cold-start blocks below for the full reasoning).
 * Showing "create a passphrase" to someone who already has one, or silently
 * unlocking the wrong vault under a matching `mode`, are the two bugs this
 * closes.
 *
 * ### Mounting
 *
 * This must be mounted ONCE, at the app root, beside the other root-mounted
 * hosts — a listener scoped inside the Vault panel never hears an FTP
 * request. Both `App.js` (root) and `VaultView` mount it; the `claim` below
 * makes that safe in either order and however many times it happens, since
 * the first instance to mount wins and every later one renders null. Two
 * hosts would mean two modals for one request, one of which answers a
 * ceremony the other is still showing.
 */

let gateOwner = null;

export default function VaultPassphraseGateHost() {
  const idRef = useRef({});
  const [owns, setOwns] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('vault');
  const [snapshot, setSnapshot] = useState(null); // { mode, initialized, itemCount }

  // Single-owner claim. StrictMode's dev double-mount releases on unmount and
  // re-claims on the second mount, so it settles on exactly one owner there too.
  useEffect(() => {
    const me = idRef.current;
    if (!gateOwner) {
      gateOwner = me;
      setOwns(true);
    }
    return () => {
      if (gateOwner === me) {
        gateOwner = null;
        setOwns(false);
      }
    };
  }, []);

  const openFor = useCallback(async (nextReason) => {
    let vaultStatus = null;
    let masterStatus = null;
    let coldStart = null;
    try {
      // Multi-device sync (PLAN.md §7): `probeColdStart()` joins the same
      // batch, but only when signed in — a device that is never going to
      // sign in has nothing on a server to check for, and probing anyway
      // would show the offline screen to every local-only user forever.
      const { isAuthenticated } = useAuthStore.getState();
      const [vs, ms, cs] = await Promise.all([
        invoke('vault_status'),
        invoke('vault_master_status'),
        isAuthenticated ? probeColdStart() : Promise.resolve(null),
      ]);
      vaultStatus = vs;
      masterStatus = ms;
      coldStart = cs;
    } catch (e) {
      // A status read that fails is not a reason to guess: showing "create a
      // passphrase" for a vault that already has one is the one mistake here
      // that could offer to overwrite something.
      console.warn('[vault] gate status read failed:', e?.message || e);
      // …but a caller that opened the gate to FINISH something is waiting on
      // an outcome event, and returning silently is the "neither" case the
      // outcome contract names: hostBridge's "save this connection to the
      // vault while locked" awaits UNLOCKED-or-DISMISSED, so a gate that
      // declines to open left that promise unresolved forever — the guest's
      // db.saveConnection never settled and its listeners leaked for the
      // session. A gate that did not open is, to a waiter, exactly a gate the
      // user dismissed: no unlock happened.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(VAULT_GATE_DISMISSED_EVENT));
      }
      return;
    }
    useVaultMasterStore.setState({ master: masterStatus });
    setSnapshot({
      mode: masterStatus?.mode === 'protected' ? 'protected' : 'keyringOnly',
      keyMissing: !!masterStatus?.keyMissing,
      // This device's own keyblock is newer, OR (not unlocked yet) the account's
      // vault is: the probe says so before any passphrase is asked (card 1.1).
      updateRequired:
        !!masterStatus?.updateRequired ||
        (!vaultStatus?.unlocked && coldStart?.decision === 'update_required'),
      backupAvailable: !!masterStatus?.backupAvailable,
      initialized: !!vaultStatus?.initialized,
      itemCount: Number(vaultStatus?.itemCount) || 0,
      unlocked: !!vaultStatus?.unlocked,
      coldStart: coldStart ? { decision: coldStart.decision, vaultId: coldStart.vaultId } : null,
    });
    setReason(nextReason || 'vault');
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!owns || !isTauri()) return undefined;
    const onGate = (event) => {
      openFor(event?.detail?.reason);
    };
    window.addEventListener(VAULT_GATE_EVENT, onGate);
    return () => window.removeEventListener(VAULT_GATE_EVENT, onGate);
  }, [owns, openFor]);

  const close = useCallback(() => {
    setOpen(false);
    // Dismissed-without-unlock. `finish` (the success path) is a separate
    // callback that never routes through here, so this cannot fire on a
    // successful unlock — a waiter listening for both events gets exactly one.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(VAULT_GATE_DISMISSED_EVENT));
    }
  }, []);

  const finish = useCallback(() => {
    setOpen(false);
    refreshVaultMaster();
    refreshVault();
    // Multi-device sync (PLAN.md §A2, call site 2): fires after EVERY
    // successful unlock or bootstrap-commit that goes through the gate —
    // this is the call site that actually matters, since unlock is
    // session-only and the gate fires at least once per session for any
    // user who ever opens the vault. Fire-and-forget; ensureVaultSync never
    // throws.
    ensureVaultSync();
    recordFeature(ACTION.VAULT_UNLOCK);   // identifier-free: the vault was unlocked today
    // Through the shared funnel as well as the gate-specific event below: this
    // is the same fact every silent unlock announces, and surfaces that cache
    // vault state should not have to know which route opened the vault.
    onVaultUnlocked();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(VAULT_GATE_UNLOCKED_EVENT, { detail: { reason } }));
      // The sidebar and banner listen for this to re-read the mode.
      window.dispatchEvent(new CustomEvent('vault-master-changed'));
    }
  }, [reason]);

  if (!owns || !isTauri() || !open || !snapshot) return null;

  // The snapshot taken when the gate opened is what decides the screen. Reading
  // the live store here instead would let a background status refresh swap the
  // modal out from under someone mid-typing — including replacing a half-typed
  // setup with an unlock prompt.
  // Card 1.1, the version gate: a keyblock from a newer Apiboo opens with no
  // secret in this build, so it gets "update Apiboo" — never a passphrase
  // prompt that is certain to fail, and never setup (which would overwrite it).
  if (snapshot.updateRequired) {
    return <VaultUpdateRequiredScreen isOpen={open} onClose={close} />;
  }

  if (snapshot.keyMissing) {
    return (
      <VaultBackupRestoreModal
        isOpen={open}
        backupAvailable={snapshot.backupAvailable}
        onClose={close}
        onRestored={finish}
      />
    );
  }

  // Multi-device sync (PLAN.md §7, review fix): `found` and `conflict` are
  // checked BEFORE the `protected` branch below, not after it. Both can
  // occur with a LOCALLY protected row: `cold_start_eligibility`'s S3
  // ("protected, zero items, vault_id differs" -> `found`/AdoptIntoExisting)
  // and S4 ("protected, live items, vault_id differs" -> `conflict`) are
  // both `mode === 'protected'` in this gate's own snapshot, since `mode`
  // reflects only whether THIS device's row has a kdf block, never whether
  // its `vault_id` matches the signed-in account's. Checking `protected`
  // first (as this used to) silently routed both states to the ordinary
  // unlock modal — for S4 that meant a device's own local passphrase would
  // unlock ITS OWN unrelated vault, after which auto-enrolment would try to
  // push it over the account's real one (closed separately, at enrolment
  // itself). `AlreadyThisAccount` (S5, `local.vault_id == remote_vault_id`)
  // deliberately does NOT preempt `protected` — it maps to `not_found`
  // precisely so the ordinary unlock modal below still wins for it (§B3).
  if (!snapshot.unlocked && snapshot.coldStart &&
      (snapshot.coldStart.decision === 'found' || snapshot.coldStart.decision === 'conflict')) {
    const { decision, vaultId } = snapshot.coldStart;
    if (decision === 'found') {
      return (
        <VaultColdStartUnlockScreen
          isOpen={open}
          vaultId={vaultId}
          onClose={close}
          onUnlocked={async (secret) => {
            try {
              await unlockColdStart(secret);
            } catch (e) {
              // The account's vault is newer than this build (card 1.1). Not a
              // wrong secret: switch to the update screen instead of the
              // no-oracle failure line.
              if (isVaultUpdateRequired(e)) {
                setSnapshot((s) => ({ ...s, updateRequired: true }));
                return;
              }
              throw e;
            }
            await finishColdStart();
            finish();
          }}
        />
      );
    }
    return <VaultColdStartConflictScreen isOpen={open} onClose={close} />;
  }

  if (snapshot.mode === 'protected') {
    return (
      <VaultPassphraseUnlockModal
        isOpen={open}
        reason={reason}
        onClose={close}
        onUnlocked={finish}
      />
    );
  }

  // Multi-device sync (PLAN.md §7): the remaining cold-start decisions —
  // `offline` and `not_found` — are unaffected by the reordering above,
  // since neither can occur alongside a locally-protected row in a way that
  // needs to preempt it: `offline` (S7) means the network check itself
  // failed, and an already-protected local vault unlocks fully offline
  // (§3's own answer — "not a new failure mode"); `not_found` (S0/S6, or
  // S5 folded per §B3) is exactly today's bootstrap-or-unlock behavior.
  if (!snapshot.unlocked && snapshot.coldStart) {
    const { decision } = snapshot.coldStart;
    if (decision === 'offline') {
      return (
        <VaultColdStartOfflineScreen
          isOpen={open}
          onClose={close}
          onRetry={() => openFor(reason)}
          // Explicit, secondary, clearly-labeled action — never the
          // default: falls through to the ORDINARY bootstrap modal only on
          // a deliberate click, not silently.
          onSetUpFreshAnyway={() => setSnapshot((s) => ({ ...s, coldStart: null }))}
        />
      );
    }
    // decision === 'not_found' falls through, unmodified, to the bootstrap
    // branch below — this is deliberate: a genuinely fresh account must
    // never see anything different from today.
  }

  // Not protected AND not currently unlocked: see the module doc comment's
  // table for why this is exactly (and only) `vault_needs_bootstrap`'s
  // state, never `master_prepare_core`'s "setup"/"migrate" territory below —
  // those require an already-unlocked vault to derive a wrap from
  // (`require_dek`), which nothing in this build can produce for an
  // unprotected scope without going through this ceremony first.
  if (!snapshot.unlocked) {
    return (
      <VaultPassphraseSetupModal
        isOpen={open}
        mode="bootstrap"
        reason={reason}
        itemCount={snapshot.itemCount}
        onClose={close}
        onDone={finish}
      />
    );
  }

  // Reachable once `device_migration.rs`'s real account-import path resolves
  // a DEK for an unprotected scope some other way — not yet built (module
  // doc comment). `snapshot.unlocked` is what makes this branch, and only
  // this branch, safe to hand to `vault_master_prepare`.
  return (
    <VaultPassphraseSetupModal
      isOpen={open}
      mode={snapshot.initialized ? 'migrate' : 'setup'}
      itemCount={snapshot.itemCount}
      onClose={close}
      onDone={finish}
    />
  );
}

/** Test seam: drop the single-owner claim between renders. */
export function __releaseGateOwner() {
  gateOwner = null;
}
